import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { Minus, Plus, X, ShoppingBag, Loader2, Lock } from 'lucide-react';
import { STORE_INFO } from '../mock';
import { useToast } from '../hooks/use-toast';
import { createCheckoutSession } from '../lib/api';
import { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQty, subtotal } = useCart();
  const { toast } = useToast();
  const [checkingOut, setCheckingOut] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '', address: '' });

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    setCheckingOut(true);
    try {
      const resp = await createCheckoutSession({
        customer,
        items: items.map(i => ({ id: i.id, name: i.name, price: i.price, qty: i.qty, flavor: i.flavor })),
        origin_url: window.location.origin,
      });
      sessionStorage.setItem('fpn_last_order_id', resp.order_id);
      window.location.href = resp.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not start checkout. Please try again.';
      toast({ title: 'Checkout failed', description: msg, variant: 'destructive' });
      setCheckingOut(false);
    }
  };

  const shipping = subtotal >= STORE_INFO.freeShipping ? 0 : 9.99;
  const total = items.length ? subtotal + shipping : 0;

  return (
    <Sheet open={isOpen} onOpenChange={(o) => { setIsOpen(o); if (!o) setShowCheckout(false); }}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-5 py-4 border-b">
          <SheetTitle className="flex items-center gap-2 text-base">
            <ShoppingBag className="w-5 h-5" /> {showCheckout ? 'Checkout' : `Your Cart (${items.length})`}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="text-center py-16 text-neutral-500">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
              <p className="font-medium">Your cart is empty</p>
              <p className="text-sm">Add some products to get started.</p>
            </div>
          ) : showCheckout ? (
            <form id="checkout-form" onSubmit={handleCheckout} className="space-y-3">
              <div><Label htmlFor="co-name">Full name *</Label><Input id="co-name" required value={customer.name} onChange={e => setCustomer({ ...customer, name: e.target.value })} className="mt-1" /></div>
              <div><Label htmlFor="co-email">Email *</Label><Input id="co-email" type="email" required value={customer.email} onChange={e => setCustomer({ ...customer, email: e.target.value })} className="mt-1" /></div>
              <div><Label htmlFor="co-phone">Phone</Label><Input id="co-phone" value={customer.phone} onChange={e => setCustomer({ ...customer, phone: e.target.value })} className="mt-1" /></div>
              <div><Label htmlFor="co-address">Shipping address</Label><Input id="co-address" value={customer.address} onChange={e => setCustomer({ ...customer, address: e.target.value })} className="mt-1" /></div>
              <div className="bg-neutral-50 rounded p-3 text-xs text-neutral-600 flex items-start gap-2 mt-3">
                <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>You&apos;ll be redirected to <strong>Stripe</strong> to enter your card details securely. Payments are processed in CAD.</span>
              </div>
            </form>
          ) : (
            <ul className="space-y-4">
              {items.map(item => (
                <li key={item.key} className="flex gap-3 pb-4 border-b last:border-0">
                  <img src={item.image} alt={item.name} className="w-16 h-16 object-contain bg-neutral-50 rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold leading-snug line-clamp-2">{item.name}</p>
                    {item.flavor && <p className="text-xs text-neutral-500 mt-0.5">Flavor: {item.flavor}</p>}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border rounded">
                        <button onClick={() => updateQty(item.key, item.qty - 1)} className="px-2 py-1 hover:bg-neutral-100"><Minus className="w-3 h-3" /></button>
                        <span className="px-3 text-sm font-medium">{item.qty}</span>
                        <button onClick={() => updateQty(item.key, item.qty + 1)} className="px-2 py-1 hover:bg-neutral-100"><Plus className="w-3 h-3" /></button>
                      </div>
                      <span className="font-bold text-sm">${(item.price * item.qty).toFixed(2)}</span>
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.key)} className="text-neutral-400 hover:text-red-500 self-start"><X className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-5 space-y-3 bg-neutral-50">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Subtotal</span>
              <span className="font-bold">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Shipping</span>
              <span className="font-semibold">{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between font-bold text-base border-t pt-2">
              <span>Total</span>
              <span>${total.toFixed(2)} CAD</span>
            </div>
            {subtotal < STORE_INFO.freeShipping && (
              <p className="text-xs text-neutral-600">Add <span className="font-bold text-[#0a0a0a]">${(STORE_INFO.freeShipping - subtotal).toFixed(2)}</span> more for free shipping!</p>
            )}
            {showCheckout ? (
              <>
                <Button type="submit" form="checkout-form" disabled={checkingOut} className="w-full bg-[#bfff00] hover:bg-[#a8e600] text-black font-bold">
                  {checkingOut ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe...</> : <><Lock className="w-4 h-4 mr-2" /> Pay ${total.toFixed(2)}</>}
                </Button>
                <Button variant="outline" onClick={() => setShowCheckout(false)} className="w-full">Back to cart</Button>
              </>
            ) : (
              <>
                <Button onClick={() => setShowCheckout(true)} className="w-full bg-[#0a0a0a] hover:bg-neutral-800 text-white font-bold">Checkout</Button>
                <Button variant="outline" onClick={() => setIsOpen(false)} className="w-full">Continue Shopping</Button>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
